import { SessionEstimate } from '@talon/web/lib/api';
import { useEstimateStore } from '@talon/web/src/context';
import { USD } from '@talon/web/src/utils/currency';
import { formatDateOnly } from '@talon/web/src/utils/dateOnly';
import { Box, Button, DateInput, Keyboard, Text, Tip } from 'grommet';
import { Star, Trash } from 'grommet-icons';
import { observer } from 'mobx-react-lite';
import { useRef, useState } from 'react';
import { borderRadiusSize } from 'src/theme';
import { DeleteModal } from '../../DeleteModal';
import { EditableText } from '../../EditableText';

export interface ISessionEstimateHeaderProps {
  sessionEstimate: SessionEstimate;
  setSessionEstimate: (sessionEstimate: SessionEstimate) => void;
}

export const SessionEstimateHeader = observer(
  ({ sessionEstimate, setSessionEstimate }: ISessionEstimateHeaderProps) => {
    /** Context **/
    const estimateStore = useEstimateStore();

    /** State **/
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [title, setTitle] = useState(sessionEstimate.title);
    // Enter saves and then blur fires too, and blur used to save even when nothing changed - each
    // save recalculating the whole estimate. Only save a title that differs from the last one saved
    const lastSavedTitle = useRef(sessionEstimate.title);
    // The SessionEstimate's own date once one has been set here, otherwise its session's
    const [date, setDate] = useState(sessionEstimate.date ?? sessionEstimate.session?.date ?? '');

    /** Methods **/
    const saveTitle = () => {
      if (title === lastSavedTitle.current) return;
      lastSavedTitle.current = title;
      setSessionEstimate({ ...sessionEstimate, title });
    };

    const deleteSessionEstimate = async () => {
      await estimateStore.deleteSessionEstimate(sessionEstimate.id);
    };

    /** Render **/
    return (
      <Box>
        <Box
          direction="row"
          gap="small"
          background={{ color: 'brand-gradient', dark: true }}
          style={{
            background: 'var(--brand-gradient)',
            borderTopLeftRadius: borderRadiusSize,
            borderTopRightRadius: borderRadiusSize,
          }}
          pad="medium"
          align="center"
        >
          <Box width="xlarge">
            {estimateStore.canEditCurrent ? (
              <EditableText
                val={title}
                onChange={setTitle}
                onSubmit={saveTitle}
                onBlur={saveTitle}
              />
            ) : (
              <Text>{title}</Text>
            )}
          </Box>
          <Box direction="row" gap="xsmall" align="center" fill="horizontal">
            <Text size="small">DATE:</Text>
            {estimateStore.canEditCurrent ? (
              <Keyboard onEnter={() => setSessionEstimate({ ...sessionEstimate, date })}>
                <DateInput
                  format="mm/dd/yyyy"
                  value={date}
                  calendarProps={{ disabled: estimateStore.getSessionEstimateDates(sessionEstimate.id) }}
                  onChange={({ value }) => setDate(typeof value === 'object' ? value[0] : value)}
                />
              </Keyboard>
            ) : (
              <Text>{formatDateOnly(date)}</Text>
            )}
          </Box>
          <Box width="small">
            {sessionEstimate.session?.is_union && (
              <Tip content="Union Session">
                <Star size="1.2rem" />
              </Tip>
            )}
          </Box>

          <Box direction="row" gap="xsmall" align="center" fill="horizontal">
            <Text size="small">SESSION WAGES:</Text>
            <Text weight="bold">{USD(sessionEstimate.subtotal)}</Text>
          </Box>
          <Box direction="row" gap="xsmall" align="center" fill="horizontal">
            <Text size="small">SESSION TOTAL:</Text>
            <Text weight="bold">{USD(sessionEstimate.subtotal_plus_fringe)}</Text>
          </Box>
          <Box width="10%" align="center">
            {estimateStore.canEditCurrent && (
              <Button
                plain
                icon={<Trash size="16px" color="red" />}
                tip={`Delete ${sessionEstimate?.title}`}
                onClick={() => setShowDeleteModal(true)}
              />
            )}
          </Box>
        </Box>
        {estimateStore.canEditCurrent && showDeleteModal && sessionEstimate && (
          <DeleteModal
            name={sessionEstimate.title}
            onDelete={deleteSessionEstimate}
            onCancel={() => setShowDeleteModal(false)}
          />
        )}
      </Box>
    );
  }
);
